import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { PageHeader, LeadSentence, Label, DisplayValue, ComboBox, ErrorInformation } from '../components'
import { useSearchPostCdCity, IRequest as ISearchPostCdCityRequest } from '../hooks/useSearchPostCdCity'
import { useSearchDistDateChangedAccept, IRequest as ISearchDistDateChangedAcceptRequest} from '../hooks/useSearchDistDateChangedAccept'
import { useRegisterDistDateChangedAccept, IRequest as IRegisterDistDateChangedAcceptRequest } from '../hooks/useRegisterDistDateChangedAccept'
import ConfirmPage from './ConfirmPage'
import CompletePage from './CompletePage'
import useMcdApiErrorData from '../hooks/useMcdApiErrorData'
import { zen2han, scrollTop } from '../modules/util'

// ページのタイトル
const title = '配達日変更受付サービス'

// ご希望時間帯「指定なし」の場合のCD
const notSpecifyDistTimezoneCd = "$DUMMY$"

const defaultValues: IRegisterDistDateChangedAcceptRequest = {
  INVOICE_NO: '', // 送り状番号
  DIST_TARGET_DATE: '', // 配達指定日付
  DIST_TARGET_TIMEZONE_CD: '', // 配達指定時間帯コード
  PKG_DROP_FLG: '0', // 置配フラグ
  PKG_DROP_CD: '', // 置配コード
  CONSIGNEE_NAME: '', // 荷受人名称
  CONSIGNEE_POSTCD: '', // 荷受人郵便番号
  CONSIGNEE_CITY_CD: '', // 荷受人市区町村コード
  CONSIGNEE_ADDR: '', // 荷受人町域番地
  CONSIGNEE_BLDG_NAME: '', // 荷受人建物名
  RESULT_NOTICE_MAIL_ADDR: '', // 結果通知メールアドレス
  RESULT_NOTICE_MAIL_ADDR_CONFIRMATION: '', // メールアドレス（確認）
}
// ↓テストデータ↓
// const defaultValues: IRegisterDistDateChangedAcceptRequest = {
//   INVOICE_NO: '', // 送り状番号
//   DIST_TARGET_DATE: '', // 配達指定日付
//   DIST_TARGET_TIMEZONE_CD: '', // 配達指定時間帯コード
//   PKG_DROP_FLG: '0', // 置配フラグ
//   PKG_DROP_CD: '', // 置配コード
//   CONSIGNEE_NAME: 'サンプル太郎', // 荷受人名称
//   CONSIGNEE_POSTCD: '1500001', // 荷受人郵便番号
//   CONSIGNEE_CITY_CD: '', // 荷受人市区町村コード
//   CONSIGNEE_ADDR: '11-2', // 荷受人町域番地
//   CONSIGNEE_BLDG_NAME: '神宮前ファッションビル３F', // 荷受人建物名
//   RESULT_NOTICE_MAIL_ADDR: 'testuser@monoplus.com', // 結果通知メールアドレス
//   RESULT_NOTICE_MAIL_ADDR_CONFIRMATION: 'testuser@monoplus.com', // メールアドレス（確認）
// }

const ErrorMessages = {
  RequireInput: '入力して下さい。',
  RequireSelect: '選択して下さい。',
  EMailNotMatch: 'メールアドレスが一致していません。',
  EMailNotFormat: '無効な形式のメールアドレスです。入力内容をご確認ください。'
}

/**
 * 配達日変更受付サービス
 */
const RequestPage: React.FC = () => {
  const linkParams = useParams<{ invoiceNo: string}>()
  const invoiceNo = linkParams.invoiceNo
  const useFormReturn = useForm<IRegisterDistDateChangedAcceptRequest>({defaultValues: {...defaultValues, INVOICE_NO: invoiceNo}})
  const { control, formState } = useFormReturn
  const { errors } = formState
  const [ddcaParams, setDdcaParams] = useState<ISearchDistDateChangedAcceptRequest>()
  const ddcaQuery = useSearchDistDateChangedAccept(ddcaParams)
  const [postCdParams, setPostCdParams] = useState<ISearchPostCdCityRequest>()
  const postCdQuery = useSearchPostCdCity(postCdParams)
  const [registerParams, setRegisterParams] = useState<IRegisterDistDateChangedAcceptRequest | undefined>()
  const { mutate: register, data: registerResult } = useRegisterDistDateChangedAccept(registerParams)
  const [warningMessage, setWarningMessage] = useState<string>('')
  const {friendlyErrorMessage, clear: clearErrorData} = useMcdApiErrorData()

  useFormReturn.watch(['PKG_DROP_FLG'])

  // 配達日変更受付検索
  useEffect(() => {
    if (linkParams) { setDdcaParams({INVOICE_NO:linkParams.invoiceNo}) }
  }, [linkParams])
  const ddcaRows = ddcaQuery.data?.data.body?._obj0
  const ddcaRow = (ddcaRows && ddcaRows.length > 0) ? ddcaRows[0] : undefined
  const delvStatus = ddcaRow?.DELV_STATUS
  const invoiceNoExistsFlg = ddcaRow?.INVOICE_NO_EXISTS_FLG // 配送伝票存在フラグ（trueなら存在）
  const invalidInvoiceNo = (ddcaQuery.data && (ddcaRow === undefined || !invoiceNoExistsFlg
    || delvStatus === "50" || delvStatus === "90")) // 配送ステータスが完了または中止の場合
  const pkgDroppableFlg = ddcaRow?.PKG_DROPPABLE_FLG // 置配可能フラグ（1なら受け取り方法を選択可能）
  const timezoneProvFlg = ddcaRow?.TIMEZONE_PROV_FLG // 時間帯提供フラグ（1なら希望時間帯を選択可能）
  const [showConfirm, setShowConfirm] = useState<boolean>(false)
  const [showComplete, setShowComplete] = useState<boolean>(false)

  // エラー情報のクリア
  const clearErrorInformation = () => {
    setWarningMessage('')
    clearErrorData()
  }
  // 郵便番号検索
  const postCdRows = postCdQuery.data?.data.body?._obj0
  const searchPostCd = async () => {
    // 郵便番号入力チェック
    const result = await useFormReturn.trigger('CONSIGNEE_POSTCD')
    if (!result) {
      return
    }
    const postCd = zen2han(useFormReturn.getValues('CONSIGNEE_POSTCD'))
    useFormReturn.setValue('CONSIGNEE_POSTCD', postCd)
    setPostCdParams({POSTCD:postCd})
    useFormReturn.setValue('CONSIGNEE_CITY_CD', '')
    useFormReturn.setValue('CONSIGNEE_ADDR', '')
  }
  useEffect(() => {
    useFormReturn.setValue('CONSIGNEE_CITY_CD', '')
    useFormReturn.setValue('CONSIGNEE_ADDR', '')
    useFormReturn.clearErrors(['CONSIGNEE_POSTCD', 'CONSIGNEE_CITY_CD'])
    if (!postCdRows) { return }
    if (postCdRows.length === 0) {
      useFormReturn.setError('CONSIGNEE_POSTCD', {message:'存在しない郵便番号です。'})
      return
    }
    // console.log({postCdRows})
    if (postCdRows.length === 1) {
      const row = postCdRows[0]
      useFormReturn.setValue('CONSIGNEE_CITY_CD', row.CITY_CD)
      useFormReturn.setValue('CONSIGNEE_ADDR', row.TOWN_AREA)
    }
  }, [postCdRows, useFormReturn])
  // 市区町村選択時に町域番地をセットする処理
  const consigneeCityCd = useFormReturn.watch('CONSIGNEE_CITY_CD')
  useEffect(() => {
    let addr= ''
    if (consigneeCityCd) {
      const row = postCdRows?.find((row) => row.CITY_CD === consigneeCityCd)
      addr = row ? row.TOWN_AREA : ''
    }
    useFormReturn.setValue('CONSIGNEE_ADDR', addr)
  }, [consigneeCityCd, postCdRows, useFormReturn])

  // 入力値検証（メールアドレス）
  const validateEMail = () => {
    const email1 = useFormReturn.getValues('RESULT_NOTICE_MAIL_ADDR')
    const email2 = useFormReturn.getValues('RESULT_NOTICE_MAIL_ADDR_CONFIRMATION')
    if (email1 || email2) {
      if (email1 !== email2) {
        return ErrorMessages.EMailNotMatch
      }
    }
    return true
  }

  // メールアドレスフォーマットチェック
  const validateEmailFormat = (value :string | undefined) => {
    if (!value) {
      return true
    } else if (value.indexOf('@') === -1 || value.match(/[^a-zA-Z0-9!"#$%&'()\*\+\-\.,\/:;<=>?@\[\\\]^_`{|}~]/g)) {
      return ErrorMessages.EMailNotFormat
    }
    return true
  }

  // 入力値検証（置配コード）
  const validatePkgDrop = () => {
    const pkgDropFlg = useFormReturn.getValues('PKG_DROP_FLG')
    const pkgDropCd = useFormReturn.getValues('PKG_DROP_CD')
    if (pkgDropFlg === '1' && !pkgDropCd) {
      return ErrorMessages.RequireSelect
    }
    return true
  }
  // 入力値検証
  const validate = async () => {
    clearErrorInformation()
    const result = await useFormReturn.trigger()
    if (!result) {
      setWarningMessage('入力に誤りがあります。入力内容を確認して下さい。')
      scrollTop()
      return
    }
    confirm()
  }
  // 確認
  const confirm = () => { setShowConfirm(true) }
  // 実行処理
  const execute = () => {
    clearErrorInformation()

    // 指定なしが選択されていた場合、空にする
    if (useFormReturn.getValues("DIST_TARGET_TIMEZONE_CD") === notSpecifyDistTimezoneCd) {
      useFormReturn.setValue("DIST_TARGET_TIMEZONE_CD", "")
    }

    const params = useFormReturn.getValues()
    setRegisterParams(params)
  }
  useEffect(() => {
    if (registerParams) { register() }
  }, [registerParams, register])
  // 実行結果処理
  useEffect(() => {
    const res = registerResult?.data
    if (!res) { return }
    if (res?.isError) {
      scrollTop()
      return
    }
    setShowConfirm(false)
    setShowComplete(true)
  }, [registerResult])

  // console.log("ddcaRow", ddcaRow)

  useEffect(() => {
    if (timezoneProvFlg === 1 && ddcaRow?.DELV_TIME !== undefined) {
      ddcaRow.DELV_TIME.forEach((item, index) => {
        if (item.DIST_TIMEZONE_CD === "") {
          ddcaRow.DELV_TIME[index].DIST_TIMEZONE_CD = notSpecifyDistTimezoneCd
          useFormReturn.setValue("DIST_TARGET_TIMEZONE_CD", notSpecifyDistTimezoneCd)
        }
      })

    }
  }, [ddcaRow])

  // 受け取り方法変更時処理
  useEffect(() => {
    if (useFormReturn.getValues('PKG_DROP_FLG') === "0") {
      useFormReturn.setValue('PKG_DROP_CD', "")
      useFormReturn.clearErrors("PKG_DROP_CD")
    }
  }, [useFormReturn.getValues('PKG_DROP_FLG')])

  // 確認ページの表示
  if (showConfirm) {
    return <ConfirmPage
      title={title}
      ddcaRow={ddcaRow}
      data={useFormReturn.getValues()}
      postCdRows={postCdRows}
      friendlyErrorMessage={friendlyErrorMessage}
      onExecute={() => { execute() }}
      onCancel={() => {
        clearErrorInformation()
        setShowConfirm(false)
      }}
    />
  }

  // 完了ページの表示
  if (showComplete) {
    return <CompletePage title={title}/>
  }

  // 入力ページの表示
  return (
    <Box>
      <PageHeader title={title} />

      <Grid container className='content-wrapper'>
        <Grid item xs={12} md={8} lg={6} className='content'>

          <LeadSentence>
            お届け先お客様情報、ご希望内容を入力して下さい。
          </LeadSentence>

          {/* 警告・エラー情報 */}
          <ErrorInformation type='warning' message={warningMessage} />
          <ErrorInformation message={friendlyErrorMessage} />

          {/* 送り状番号 */}
          <Box
            sx={{
              mb: 2
            }}
          >
            <Typography component='h2'
              sx={{
                backgroundColor: '#d0e6f2',
                fontSize: '1rem',
                textAlign: 'center',
                p: 1,
              }}>
              送り状番号：
              <Typography component='span' sx={{fontSize:'1.5em', fontWeight:'bold'}}>{invoiceNo}</Typography>
            </Typography>
            { invalidInvoiceNo && <ErrorInformation message='無効な送り状番号です。' /> }
          </Box>

          {/* お届け先お客様情報 */}
          <Box>
            <Box
              sx={{
                my: 2,
                p: 1,
                backgroundColor: '#0002'
              }}
            >
              <Typography component='h2'>■お届け先お客様情報</Typography>
            </Box>

            <Grid container spacing={1}
              sx={{
                alignItems: 'center'
              }}
            >

              <Grid item xs={12} md={4}><Label required>お名前（漢字）：</Label></Grid>
              <Grid item xs={12} md={8}>
                <Controller
                  name='CONSIGNEE_NAME'
                  control={control}
                  rules={{required: ErrorMessages.RequireInput}}
                  render={({field}) =>
                    <TextField
                      {...field}
                      size='small'
                      fullWidth
                      required
                      disabled={invalidInvoiceNo}
                      inputProps={{
                        maxLength: '100',
                      }}
                      error={errors.CONSIGNEE_NAME !== undefined}
                      helperText={errors.CONSIGNEE_NAME?.message}
                    />
                  }
                />
              </Grid>

              <Grid item xs={12} md={4}><Label required>郵便番号：</Label></Grid>
              <Grid item xs={12} md={8}>
                <Controller
                  name='CONSIGNEE_POSTCD'
                  control={control}
                  rules={{required: ErrorMessages.RequireInput}}
                  render={({field}) =>
                    <TextField
                      {...field}
                      size='small'
                      sx={{
                        mr: 1,
                        mb: 1,
                      }}
                      required
                      disabled={invalidInvoiceNo}
                      inputProps={{
                        inputMode: 'numeric',
                        maxLength: '7',
                      }}
                      error={errors.CONSIGNEE_POSTCD !== undefined}
                      helperText={errors.CONSIGNEE_POSTCD?.message}
                    />
                }/>
                <Button
                  size='small'
                  sx={{
                    fontSize: '1em',
                    fontWeight: 'normal',
                    height: '40px',
                  }}
                  variant='outlined'
                  disabled={invalidInvoiceNo}
                  onClick={() => { searchPostCd() }}
                >住所検索</Button>
              </Grid>

              <Grid item xs={12} md={4}><Label required>市区町村：</Label></Grid>
              <Grid item xs={12} md={8}>
                <Controller
                  name='CONSIGNEE_CITY_CD'
                  control={control}
                  rules={{required: ErrorMessages.RequireSelect}}
                  render={({field}) =>
                    <ComboBox
                      {...field}
                      variant='outlined'
                      size='small'
                      fullWidth
                      disabled={invalidInvoiceNo}
                      data={postCdRows}
                      valueName='CITY_CD'
                      displayName='CITY_NAME'
                      error={errors.CONSIGNEE_CITY_CD !== undefined}
                      helperText={errors.CONSIGNEE_CITY_CD?.message}
                      placeholder={(postCdRows && postCdRows.length > 1) ? '選択して下さい。' : ''}
                      ref={null}
                    />
                }/>
              </Grid>

              <Grid item xs={12} md={4}><Label required>町域番地：</Label></Grid>
              <Grid item xs={12} md={8}>
                <Controller
                  name='CONSIGNEE_ADDR'
                  control={control}
                  rules={{required: ErrorMessages.RequireInput}}
                  render={({field}) =>
                    <TextField
                      {...field}
                      size='small'
                      fullWidth
                      required
                      disabled={invalidInvoiceNo}
                      inputProps={{
                        maxLength: '100',
                      }}
                      error={errors.CONSIGNEE_ADDR !== undefined}
                      helperText={errors.CONSIGNEE_ADDR?.message}
                    />
                }/>
              </Grid>

              <Grid item xs={12} md={4}><Label>建物名：</Label></Grid>
              <Grid item xs={12} md={8}>
                <Controller
                  name='CONSIGNEE_BLDG_NAME'
                  control={control}
                  render={({field}) =>
                    <TextField
                      {...field}
                      size='small'
                      fullWidth
                      disabled={invalidInvoiceNo}
                      inputProps={{
                        maxLength: '100',
                      }}
                    />
                }/>
              </Grid>

              <Grid item xs={12} md={4}><Label>メールアドレス：</Label></Grid>
              <Grid item xs={12} md={8}>
                <Controller
                  name='RESULT_NOTICE_MAIL_ADDR'
                  control={control}
                  rules={{ validate:
                      {
                        valueFormat : inputVal =>  validateEmailFormat(inputVal),
                        valueEqual :validateEMail
                      }
                }}
                  render={({field}) =>
                    <TextField
                      {...field}
                      type='text'
                      size='small'
                      fullWidth
                      disabled={invalidInvoiceNo}
                      inputProps={{
                        maxLength: '200',
                      }}
                      error={errors.RESULT_NOTICE_MAIL_ADDR !== undefined}
                      helperText={errors.RESULT_NOTICE_MAIL_ADDR?.message}
                    />
                }/>
              </Grid>

              <Grid item xs={12} md={4}><Label>メールアドレス（確認）：</Label></Grid>
              <Grid item xs={12} md={8}>
                <Controller
                  name='RESULT_NOTICE_MAIL_ADDR_CONFIRMATION'
                  control={control}
                  rules={{ validate: {
                      valueFormat : inputVal =>  validateEmailFormat(inputVal),
                      valueEqual :validateEMail
                  } }}
                  render={({field}) =>
                    <TextField
                      {...field}
                      type='text'
                      size='small'
                      fullWidth
                      disabled={invalidInvoiceNo}
                      inputProps={{
                        maxLength: '200',
                      }}
                      error={errors.RESULT_NOTICE_MAIL_ADDR_CONFIRMATION !== undefined}
                      helperText={errors.RESULT_NOTICE_MAIL_ADDR_CONFIRMATION?.message}
                    />
                }/>
              </Grid>
              <Grid item xs={12} md={4}></Grid>
              <Grid item xs={12} md={8}>
                <Typography component='p' sx={{fontSize:'0.8rem'}}>※お荷物に関するご案内メールをご希望の場合のみ、ご入力下さい。</Typography>
              </Grid>

            </Grid>
          </Box>

          {/* ご希望内容 */}
          <Box>
            <Box
              sx={{
                my: 2,
                p: 1,
                backgroundColor: '#0002'
              }}
            >
              <Typography component='h2'>■ご希望内容</Typography>
            </Box>

            <Grid container spacing={1}
              sx={{
                alignItems: 'center'
              }}
            >

              <Grid item xs={12} md={4}><Label required>お受け取り方法：</Label></Grid>
              <Grid item xs={12} md={8}>
                {
                  (pkgDroppableFlg === 0) ?
                    <DisplayValue>※指定不可（ご自宅）</DisplayValue>
                    :
                    <Controller
                      name='PKG_DROP_FLG'
                      control={control}
                      rules={{required: ErrorMessages.RequireSelect}}
                      render={({field}) =>
                        <RadioGroup
                          {...field}
                        >
                          <Grid container>
                            <Grid item xs={12} md={3}>
                              <FormControlLabel value={0} control={<Radio />} label='ご自宅' disabled={invalidInvoiceNo} />
                            </Grid>
                            <Grid item xs={12} md={9}>
                              <FormControlLabel value={1} control={<Radio />} label='置き配' disabled={invalidInvoiceNo} />
                              <Controller
                                name='PKG_DROP_CD'
                                control={control}
                                rules={{validate: validatePkgDrop}}
                                render={({field}) =>
                                  <ComboBox
                                    {...field}
                                    variant='outlined'
                                    size='small'
                                    fullWidth
                                    data={ddcaRow?.PKG_DROP}
                                    valueName='PKG_DROP_CD'
                                    displayName='PKG_DROP_NAME'
                                    emptyItem
                                    error={errors.PKG_DROP_CD !== undefined}
                                    helperText={errors.PKG_DROP_CD?.message}
                                    disabled={invalidInvoiceNo || useFormReturn.getValues('PKG_DROP_FLG') === '0'}
                                    ref={null}
                                  />
                              }/>

                            </Grid>
                          </Grid>
                        </RadioGroup>
                      }/>
                    }
              </Grid>

              <Grid item xs={12} md={4}><Label required>ご希望日：</Label></Grid>
              <Grid item xs={12} md={8}>
                <Controller
                  name='DIST_TARGET_DATE'
                  control={control}
                  rules={{required: ErrorMessages.RequireSelect}}
                  render={({field}) =>
                    <ComboBox
                      {...field}
                      size='small'
                      variant='outlined'
                      fullWidth
                      disabled={invalidInvoiceNo}
                      data={ddcaRow?.DESIRED_DATE_LIST}
                      valueName='DESIRED_DATE'
                      displayName='DESIRED_DATE_FORMATTED'
                      emptyItem
                      error={errors.DIST_TARGET_DATE !== undefined}
                      helperText={errors.DIST_TARGET_DATE?.message}
                      ref={null}
                    />
                }/>
              </Grid>

              <Grid item xs={12} md={4}><Label>ご希望時間帯：</Label></Grid>
              <Grid item xs={12} md={8}>
              {
                (timezoneProvFlg === 1) ?
                    <Controller
                      name='DIST_TARGET_TIMEZONE_CD'
                      control={control}
                      // rules={{required: ErrorMessages.RequireSelect}}
                      render={({field}) =>
                        <ComboBox
                          {...field}
                          size='small'
                          variant='outlined'
                          fullWidth
                          disabled={invalidInvoiceNo}
                          data={ddcaRow?.DELV_TIME}
                          valueName='DIST_TIMEZONE_CD'
                          displayName='DIST_TIMEZONE_NAME'
                          emptyItem={false}
                          error={errors.DIST_TARGET_TIMEZONE_CD !== undefined}
                          helperText={errors.DIST_TARGET_TIMEZONE_CD?.message}
                          ref={null}
                        />
                    }/>
                  :
                    <DisplayValue>※指定不可</DisplayValue>
              }
              </Grid>

            </Grid>
          </Box>

          {/* 入力内容の確認へ */}
          <Box>
            <Grid container
              sx={{
                mt: 4,
                mb: 1,
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Grid item xs={12} md={6}>
                <Button
                  size='large'
                  variant='contained'
                  fullWidth
                  disabled={invalidInvoiceNo}
                  onClick={() => { validate() }}
                >入力内容の確認へ</Button>
              </Grid>
            </Grid>
          </Box>

        </Grid>
      </Grid>

    </Box>
  )
}

export default RequestPage
